import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  getAgentStatus,
  createAgent,
  startAgent,
  stopAgent,
  rebuildAgent,
  deleteAgent,
  getSpend,
  provisionOpenRouter,
  getTelegramBot,
  saveTelegramBot,
  patchTelegramBot,
  deleteTelegramBot,
  applyConfig,
  type AgentStatus,
  type AgentStatusResponse,
  type SpendResponse,
  type TelegramBotStatus,
  ApiRequestError,
} from "@app/lib/api";

const REBUILD_TYPES = ["cx22", "cx32", "cpx21", "cpx31"] as const;

function statusTone(s: AgentStatus): "ok" | "warn" | "down" | "idle" {
  if (s === "running") return "ok";
  if (s === "creating" || s === "awaiting_phone_home" || s === "provision_requested") return "warn";
  if (s === "error") return "down";
  return "idle";
}

function statusLabel(s: AgentStatus): string {
  switch (s) {
    case "none": return "Not provisioned";
    case "provision_requested": return "Queued";
    case "creating": return "Creating";
    case "awaiting_phone_home": return "Booting";
    case "running": return "Running";
    case "stopped": return "Stopped";
    case "error": return "Error";
  }
}

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function Dashboard() {
  const [status, setStatus] = useState<AgentStatusResponse | null>(null);
  const [spend, setSpend] = useState<SpendResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await getAgentStatus();
      setStatus(s);
    } catch (e) {
      if (e instanceof Error) setErr(e.message);
    }
    try {
      const sp = await getSpend();
      setSpend(sp);
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 404) {
        setSpend(null);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 10_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const wrap = async (label: string, fn: () => Promise<unknown>) => {
    setErr(null);
    setBusy(label);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const s = status?.status ?? "none";
  const tone = statusTone(s);
  const toneClass = {
    ok: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    warn: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    down: "bg-red-500/15 text-red-400 border-red-500/30",
    idle: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  }[tone];

  const canStart = s === "stopped";
  const canStop = s === "running";
  const canRebuild = s === "stopped" || s === "running";
  const canDelete = s !== "none";

  const usagePct = spend
    ? Math.min(100, Math.round((spend.usageCents / Math.max(1, spend.limitCents)) * 100))
    : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold">Agent</h1>

      {err ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {err}
        </div>
      ) : null}

      {status === null ? (
        <div className="text-sm text-muted-fg">Loading…</div>
      ) : s === "none" ? (
        <CreateSection onCreated={refresh} />
      ) : (
        <>
          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>Hermes VM</CardTitle>
                <CardDescription>
                  {status?.region ? `${status.region} - ${status.serverType ?? "?"}` : "No VM yet"}
                </CardDescription>
              </div>
              <span
                className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${toneClass}`}
              >
                {statusLabel(s)}
              </span>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-xs text-muted-fg">
                Last phone-home:{" "}
                {status?.lastPhoneHomeAt ? new Date(status.lastPhoneHomeAt).toLocaleString() : "never"}
              </div>
              {status?.publicIp && (
                <div className="flex flex-col gap-1">
                  <div className="text-xs text-muted-fg">SSH tunnel to Hermes dashboard:</div>
                  <code className="select-all rounded bg-muted px-2 py-1 text-xs">
                    {"ssh -N -L 9119:localhost:9119 -p 22222 root@"}
                    {status.publicIp}
                  </code>
                  <div className="text-xs text-muted-fg">
                    Then open{" "}
                    <a
                      href="http://localhost:9119"
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:underline"
                    >
                      http://localhost:9119
                    </a>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canStart || busy !== null}
                  onClick={() => wrap("start", startAgent)}
                >
                  {busy === "start" ? "Starting..." : "Start"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canStop || busy !== null}
                  onClick={() => wrap("stop", stopAgent)}
                >
                  {busy === "stop" ? "Stopping..." : "Stop"}
                </Button>
                <RebuildPicker
                  disabled={!canRebuild || busy !== null}
                  currentType={status?.serverType ?? null}
                  onPick={(t) => wrap("rebuild", () => rebuildAgent(t))}
                  busy={busy === "rebuild"}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canDelete || busy !== null}
                  onClick={() => {
                    if (!window.confirm("Delete this VM? The volume is retained.")) return;
                    wrap("delete", deleteAgent);
                  }}
                >
                  {busy === "delete" ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <TelegramSection onApplied={refresh} />
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>LLM spend</CardTitle>
          <CardDescription>
            Billing and credits are not active in this phase.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {spend ? (
            <div className="space-y-2">
              <div className="text-sm">
                {formatUsd(spend.usageCents)} of {formatUsd(spend.limitCents)} (
                {formatUsd(spend.remainingCents)} remaining)
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-accent"
                  style={{ width: `${usagePct}%` }}
                  aria-label={`Spend ${usagePct}%`}
                />
              </div>
              <div className="text-xs text-muted-fg">Source: {spend.source}</div>
            </div>
          ) : (
            <div className="text-sm text-muted-fg">No OpenRouter key yet (created on first VM).</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VM creation form — shown when no VM exists; Telegram token is required
// ---------------------------------------------------------------------------

function CreateSection({ onCreated }: { onCreated: () => void }) {
  const [token, setToken] = useState("");
  const [allowedUsers, setAllowedUsers] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const wildcardWarning =
    allowedUsers.trim() === "*"
      ? "Anyone who finds your bot username can talk to your agent. Enter your numeric Telegram user ID to restrict access."
      : null;

  const handleCreate = async () => {
    setErr(null);
    setBusy(true);
    try {
      await saveTelegramBot({
        token,
        ...(allowedUsers.trim() ? { telegramAllowedUsers: allowedUsers.trim() } : {}),
      });
      try {
        await provisionOpenRouter();
      } catch (e) {
        if (!(e instanceof ApiRequestError && e.status === 409)) throw e;
      }
      await createAgent();
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create VM</CardTitle>
        <CardDescription>
          Connect a Telegram bot first — the token is baked into the VM at creation time and cannot be added later without a rebuild.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {err ? (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {err}
          </div>
        ) : null}

        <div className="space-y-3">
          <div className="text-sm font-medium">
            Telegram bot <span className="text-red-400">*</span>
          </div>
          <div className="text-xs text-muted-fg">
            Create a bot at @BotFather on Telegram, copy the token, and paste it below.
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-3">
            <Label htmlFor="ct-token">Bot token</Label>
            <Input
              id="ct-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="1234567890:ABCdef…"
              autoComplete="off"
            />
          </div>
          <div className="grid grid-cols-[140px_1fr] items-start gap-3">
            <Label htmlFor="ct-allowed" className="pt-2 text-sm">
              Allowed users{" "}
              <span className="text-muted-fg">(optional)</span>
            </Label>
            <div className="flex flex-col gap-1">
              <Input
                id="ct-allowed"
                value={allowedUsers}
                onChange={(e) => setAllowedUsers(e.target.value)}
                placeholder="Your Telegram user ID or * for anyone"
              />
              <div className="text-xs text-muted-fg">
                Restricts the bot to your Telegram account. Find your numeric ID via @userinfobot.
              </div>
              {wildcardWarning ? (
                <div className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-300">
                  ⚠ {wildcardWarning}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <Button disabled={!token.trim() || busy} onClick={handleCreate}>
          {busy ? "Creating..." : "Create VM"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Telegram management card — shown when a VM exists
// ---------------------------------------------------------------------------

function TelegramSection({ onApplied }: { onApplied: () => void }) {
  const [botStatus, setBotStatus] = useState<TelegramBotStatus | null>(null);
  const [token, setToken] = useState("");
  const [allowedUsers, setAllowedUsers] = useState("");
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBotStatus(await getTelegramBot());
    } catch {
      // stay in loading state silently
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (botStatus?.configured) {
      setAllowedUsers(botStatus.telegramAllowedUsers ?? "");
    }
  }, [botStatus]);

  const onSave = async () => {
    setErr(null);
    setSaving(true);
    try {
      await saveTelegramBot({
        token,
        ...(allowedUsers.trim() ? { telegramAllowedUsers: allowedUsers.trim() } : {}),
      });
      setToken("");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove this bot? The token stays active on the VM until Apply & Rebuild.")) return;
    setErr(null);
    try {
      await deleteTelegramBot();
      setToken("");
      setAllowedUsers("");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const onApply = async () => {
    if (
      !window.confirm(
        "Apply & Rebuild VM?\n\nThis recreates the VM with fresh config. Takes ~3 minutes. The volume is kept.",
      )
    )
      return;
    setErr(null);
    setApplying(true);
    try {
      await applyConfig();
      onApplied();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setApplying(false);
    }
  };

  const wildcardWarning =
    allowedUsers.trim() === "*"
      ? "Anyone who finds your bot username can talk to your agent. Enter your numeric Telegram user ID to restrict access."
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Telegram</CardTitle>
        <CardDescription>
          Changes take effect after Apply &amp; Rebuild.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {err ? (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {err}
          </div>
        ) : null}

        {botStatus === null ? (
          <div className="text-sm text-muted-fg">Loading…</div>
        ) : botStatus.configured ? (
          <>
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm font-medium">
                @{botStatus.username}{" "}
                <span className="text-emerald-400">✓</span>
              </div>
              <Button size="sm" variant="outline" onClick={onDelete}>
                Remove
              </Button>
            </div>

            <div className="grid grid-cols-[160px_1fr] items-start gap-3">
              <Label htmlFor="ts-allowed" className="pt-2 text-sm">
                Allowed users
              </Label>
              <div className="flex flex-col gap-1">
                <Input
                  id="ts-allowed"
                  value={allowedUsers}
                  onChange={(e) => setAllowedUsers(e.target.value)}
                  placeholder="Your Telegram user ID or * for anyone"
                />
                <div className="text-xs text-muted-fg">
                  Find your numeric ID by messaging @userinfobot on Telegram.
                </div>
                {wildcardWarning ? (
                  <div className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-300">
                    ⚠ {wildcardWarning}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={async () => {
                  setErr(null);
                  setSaving(true);
                  try {
                    await patchTelegramBot(allowedUsers.trim() || null);
                    await load();
                  } catch (e) {
                    setErr(e instanceof Error ? e.message : String(e));
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? "Saving…" : "Update allowlist"}
              </Button>
            </div>

            <div className="rounded border border-border bg-muted/30 px-3 py-2 text-xs text-muted-fg">
              Changes take effect on next Apply &amp; Rebuild.
            </div>

            <Button size="sm" disabled={applying} onClick={onApply}>
              {applying ? "Rebuilding…" : "Apply & Rebuild VM"}
            </Button>
            <div className="text-xs text-muted-fg">
              Recreates the VM with updated config. Volume is kept. Takes ~3 minutes.
            </div>
          </>
        ) : (
          <>
            <div className="text-xs text-muted-fg mb-1">No bot configured. Add one below.</div>
            <div className="grid grid-cols-[160px_1fr] items-center gap-3">
              <Label htmlFor="ts-token">Bot token</Label>
              <Input
                id="ts-token"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="1234567890:ABCdef…"
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-[160px_1fr] items-start gap-3">
              <Label htmlFor="ts-allowed-new" className="pt-2 text-sm">
                Allowed users{" "}
                <span className="text-muted-fg">(optional)</span>
              </Label>
              <div className="flex flex-col gap-1">
                <Input
                  id="ts-allowed-new"
                  value={allowedUsers}
                  onChange={(e) => setAllowedUsers(e.target.value)}
                  placeholder="Your Telegram user ID or * for anyone"
                />
                <div className="text-xs text-muted-fg">
                  Restricts the bot to your account only. Find your numeric ID via @userinfobot.
                </div>
                {wildcardWarning ? (
                  <div className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-300">
                    ⚠ {wildcardWarning}
                  </div>
                ) : null}
              </div>
            </div>

            <Button size="sm" disabled={saving || !token.trim()} onClick={onSave}>
              {saving ? "Saving…" : "Save bot"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface RebuildPickerProps {
  disabled: boolean;
  busy: boolean;
  currentType: string | null;
  onPick: (newType: string) => void;
}

function RebuildPicker({ disabled, busy, currentType, onPick }: RebuildPickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <Button
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {busy ? "Rebuilding..." : "Rebuild"}
      </Button>
      {open ? (
        <div className="absolute z-10 mt-1 flex flex-col rounded-md border border-border bg-bg p-1 text-sm shadow">
          {REBUILD_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className="px-3 py-1 text-left hover:bg-muted disabled:opacity-50"
              disabled={t === currentType}
              onClick={() => {
                setOpen(false);
                onPick(t);
              }}
            >
              {t}
              {t === currentType ? " (current)" : ""}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
